/**
 * @file This module is responsible for ensuring the correct
 *     tracking is used for the current Ad placement conditions.
 */
import { layoutMatcher } from 'shared/layoutMatcher';
import { sizeMatcher } from 'shared/layoutMatcher/layoutMatcher';
import { detectPlatform, getAdOrientation } from 'shared/utils/adContext';
import { getWindowSize } from 'shared/utils/dom';
import { values } from 'shared/utils/misc';
import { replaceMacros } from 'shared/utils/urls/misc';
/**
 * Process the provided TrackingFields, and update their
 * `value` to only include the highest-matching TrackingCondition.
 */
export const matchTracking = (fields) => {
    var _a, _b;
    const { width, height } = getWindowSize();
    const orientation = getAdOrientation(width, height);
    const platform = detectPlatform();
    const layouts = values(window.ADAPT.ad.layouts);
    const layout = layoutMatcher(width, height, layouts).id;
    const pxSize = sizeMatcher(width, height, layouts);
    const size = `${(_a = pxSize) === null || _a === void 0 ? void 0 : _a.w}x${(_b = pxSize) === null || _b === void 0 ? void 0 : _b.h}`;
    const currentConditions = {
        platform,
        orientation,
        layout,
        size,
    };
    const trackingMatcher = createTrackingMatcher(currentConditions);
    // Filter for current conditions and replace all {{macros}}.
    return fields.map(trackingMatcher);
};
/**
 * Injects the correct impression tracking tags
 * for the current platform, layout, etc.
 */
export const injectImpressionTracking = (fields) => {
    var _a, _b;
    const impressionField = fields.find(f => f.key === 'stage' && f.type === 'impression');
    const impressionTags = (_b = (_a = impressionField) === null || _a === void 0 ? void 0 : _a.value[0]) === null || _b === void 0 ? void 0 : _b.tags;
    if (!impressionTags) {
        return;
    }
    const trackingElement = document.createElement('div');
    trackingElement.style.position = 'absolute';
    trackingElement.style.visibility = 'hidden';
    trackingElement.style.pointerEvents = 'none';
    trackingElement.style.zIndex = '-1';
    trackingElement.innerHTML = replaceMacros(impressionTags);
    injectElement(trackingElement);
};
/**
 * Inject an HTML tag and its children, ensuring that `<script />` tags are executed.
 */
const injectElement = (element, parent = document.body) => {
    let elementClone;
    // Special handling for script tags
    if (isScriptElement(element)) {
        const script = document.createElement('script');
        script.async = element.async;
        script.src = element.src;
        elementClone = script;
    }
    else {
        elementClone = element.cloneNode(false);
    }
    for (let i = 0; i < element.childElementCount; i++) {
        injectElement(element.children[i], elementClone);
    }
    parent.appendChild(elementClone);
};
/**
 * Type guard to determine if the provided Element is an HTMLScriptElement.
 */
const isScriptElement = (element) => element.tagName.toLowerCase() === 'script';
/**
 * Creates a tracking matcher that will take a TrackingField and
 * restrict its `value` to only the best-matching TrackingValue.
 */
const createTrackingMatcher = (currentConditions) => (trackingField) => (Object.assign(Object.assign({}, trackingField), { value: matchTrackingValue(currentConditions, 
    // @ts-ignore
    trackingField.value) }));
export const matchTrackingValue = (currentConditions, trackingConditions) => {
    const { orientation, platform, layout, size } = currentConditions;
    const points = createPointCalculator(currentConditions);
    const topMatch = trackingConditions
        // Ensure only matching conditions are considered.
        .filter(t => (!t.platform || t.platform === platform) &&
        (!t.orientation || t.orientation === orientation) &&
        (!t.layout || t.layout === layout) &&
        (!t.size || t.size === size))
        // Find the best match.
        .reduce((top, set) => {
        if (top === undefined) {
            return set;
        }
        return points(top) >= points(set) ? top : set;
    }, undefined);
    return topMatch ? [topMatch] : [];
};
const createPointCalculator = (currentConditions) => (set) => {
    const { layout, orientation, platform, size } = currentConditions;
    const matchesPlatform = platform === set.platform;
    const matchesOrientation = orientation === set.orientation;
    const matchesLayout = layout === set.layout;
    const matchesSize = size === set.size;
    return (+matchesPlatform + +matchesOrientation + +matchesLayout + +matchesSize); // +true = 1, +false = 0
};
